import React from 'react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Testimonial from '../components/ui/Testimonial';
import { CheckIcon, ClockIcon, HeartIcon, ShieldIcon, SmartphoneIcon, CalendarIcon, BellIcon, UsersIcon, FileTextIcon, CreditCardIcon } from 'lucide-react';
export default function Home() {
  const features = [{
    icon: ClockIcon,
    title: 'Medication & Appointment Tracking',
    description: 'Keep track of all medications and appointments in one simple place.'
  }, {
    icon: BellIcon,
    title: 'SMS and Call Alerts',
    description: 'Choose between phone calls or text messages for reminders that work best.'
  }, {
    icon: UsersIcon,
    title: 'Family Monitoring Dashboard',
    description: 'Let family members see when reminders are confirmed and stay informed.'
  }, {
    icon: FileTextIcon,
    title: 'GP Visit & Prescription Notes',
    description: 'Store important notes about doctor visits and prescriptions securely.'
  }, {
    icon: CreditCardIcon,
    title: 'Simple Monthly or Annual Payment',
    description: 'Straightforward pricing with no hidden fees or complicated contracts.'
  }];
  const pricingPlans = [{
    name: 'Silver',
    price: '£99',
    period: 'per year',
    description: 'Perfect for individuals',
    features: ['Daily SMS reminders', 'Standard call reminders', '1 family member dashboard'],
    stripeLink: 'https://buy.stripe.com/fZu5kEa7H65t4dAbcw2B200'
  }, {
    name: 'Gold',
    price: '£120',
    period: 'per year',
    description: 'Most popular choice',
    features: ['Unlimited reminders', 'Up to 3 family members', 'Carer note sharing'],
    highlighted: true,
    stripeLink: 'https://buy.stripe.com/7sY4gA0x7fG325sa8s2B201'
  }, {
    name: 'Platinum',
    price: '£150',
    period: 'per year',
    description: 'Complete family support',
    features: ['Unlimited family members', 'Priority support', 'Monthly wellbeing summary'],
    stripeLink: 'https://buy.stripe.com/3cI5kE4Nn9hF25s80k2B202'
  }];
  const testimonials = [{
    quote: 'This app helped my mum never miss her tablets again.',
    author: 'H. Collins'
  }, {
    quote: 'Finally something that helps me care for dad from miles away.',
    author: 'J. Harris'
  }, {
    quote: 'Clear reminders and easy to use.',
    author: 'S. Patel'
  }];
  const trustMarkers = ['UK-Based', 'Data Secure', 'GDPR Compliant', 'Trusted by Families'];
  return <div className="w-full" data-id="element-317">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-light-grey" data-id="element-318">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-id="element-319">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-320">
            <div data-id="element-321">
              <h1 className="text-4xl md:text-5xl font-bold text-navy font-heading mb-4" data-id="element-322">
                Helping Families Stay on Track with Medication & Appointments
              </h1>
              <p className="text-lg text-dark mb-8" data-id="element-323">
                Simple daily reminders that keep everyone informed and
                stress-free.
              </p>
              <div className="flex flex-wrap gap-4" data-id="element-324">
                <Button size="lg" href="/contact" data-id="element-325">
                  Get Started
                </Button>
                <Button variant="outline" size="lg" href="/features" data-id="element-326">
                  See How It Works
                </Button>
              </div>
            </div>
            <div className="flex justify-center" data-id="element-327">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/aniuAS6PpJ8BczVpHjyYVq/1.jpg" alt="Family gathering together during the holidays" className="rounded-lg shadow-lg max-w-full h-auto" data-id="element-328" />
            </div>
          </div>
        </div>
      </section>
      {/* Pricing Preview Section */}
      <section className="py-16 bg-white" data-id="element-329">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-330">
          <div className="text-center mb-12" data-id="element-331">
            <h2 className="text-3xl font-bold text-navy font-heading mb-4" data-id="element-332">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-dark max-w-3xl mx-auto" data-id="element-333">
              Choose the plan that works best for your family. All plans include
              secure payments through Stripe.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-334">
            {pricingPlans.map((plan, index) => <Card key={index} className={`text-center ${plan.highlighted ? 'ring-2 ring-sky-blue shadow-xl' : ''}`} data-id="element-335">
                {plan.highlighted && <div className="bg-sky-blue text-white text-xs font-semibold py-1 px-3 rounded-full inline-block mb-4" data-id="element-336">
                    Most Popular
                  </div>}
                <h3 className="text-2xl font-bold text-navy mb-2" data-id="element-337">
                  {plan.name}
                </h3>
                <div className="mb-2" data-id="element-338">
                  <span className="text-4xl font-bold text-navy" data-id="element-339">
                    {plan.price}
                  </span>
                  <span className="text-dark text-sm ml-1" data-id="element-340">{plan.period}</span>
                </div>
                <p className="text-dark text-sm mb-6" data-id="element-341">{plan.description}</p>
                <ul className="space-y-2 mb-6 text-left" data-id="element-342">
                  {plan.features.map((feature, featureIndex) => <li key={featureIndex} className="flex items-start text-sm" data-id="element-343">
                      <CheckIcon className="h-4 w-4 text-sky-blue mr-2 flex-shrink-0 mt-0.5" data-id="element-344" />
                      <span className="text-dark" data-id="element-345">{feature}</span>
                    </li>)}
                </ul>
                <a href={plan.stripeLink} className={`block px-6 py-3 rounded-lg font-semibold transition-colors ${plan.highlighted ? 'bg-sky-blue text-white hover:bg-sky-blue/90' : 'bg-navy text-white hover:bg-navy/90'}`} target="_blank" rel="noopener noreferrer" data-id="element-346">
                  Pay Now
                </a>
              </Card>)}
          </div>
          <div className="text-center mt-12" data-id="element-347">
            <Button size="lg" href="/pricing" data-id="element-348">
              View Full Pricing Details
            </Button>
            <p className="text-sm text-dark/60 mt-4" data-id="element-349">
              14-day money-back guarantee • No hidden fees
            </p>
          </div>
        </div>
      </section>
      {/* Problem Statement */}
      <section className="py-16 bg-light-grey" data-id="element-350">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-351">
          <h2 className="text-3xl font-bold text-navy font-heading mb-6" data-id="element-352">
            We Understand the Challenge
          </h2>
          <p className="text-lg text-dark mb-4" data-id="element-353">
            Many older adults forget medication or appointments. Families worry,
            carers get overwhelmed.
          </p>
          <p className="text-xl font-semibold text-navy" data-id="element-354">
            Artisanfix keeps things organised with smart daily reminders.
          </p>
        </div>
      </section>
      {/* How It Works */}
      <section className="py-16 bg-white" data-id="element-355">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-356">
          <div className="text-center mb-16" data-id="element-357">
            <h2 className="text-3xl font-bold text-navy font-heading mb-4" data-id="element-358">
              How It Works
            </h2>
            <p className="text-lg text-dark max-w-3xl mx-auto" data-id="element-359">
              Getting started with Artisanfix is simple and straightforward.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-360">
            <Card className="text-center relative" data-id="element-361">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-navy text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold" data-id="element-362">
                1
              </div>
              <div className="pt-8" data-id="element-363">
                <div className="rounded-full bg-light-grey p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4" data-id="element-364">
                  <ClockIcon className="h-8 w-8 text-navy" data-id="element-365" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-3" data-id="element-366">
                  Set Up Reminders
                </h3>
                <p className="text-dark" data-id="element-367">
                  Add medication times and appointment dates in just a few
                  clicks.
                </p>
              </div>
            </Card>
            <Card className="text-center relative" data-id="element-368">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-navy text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold" data-id="element-369">
                2
              </div>
              <div className="pt-8" data-id="element-370">
                <div className="rounded-full bg-light-grey p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4" data-id="element-371">
                  <SmartphoneIcon className="h-8 w-8 text-navy" data-id="element-372" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-3" data-id="element-373">
                  Choose Alert Type
                </h3>
                <p className="text-dark" data-id="element-374">
                  Select phone call or SMS alerts based on what works best.
                </p>
              </div>
            </Card>
            <Card className="text-center relative" data-id="element-375">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-navy text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold" data-id="element-376">
                3
              </div>
              <div className="pt-8" data-id="element-377">
                <div className="rounded-full bg-light-grey p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4" data-id="element-378">
                  <CheckIcon className="h-8 w-8 text-navy" data-id="element-379" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-3" data-id="element-380">
                  Get Instant Updates
                </h3>
                <p className="text-dark" data-id="element-381">
                  Receive confirmation when reminders are acknowledged.
                </p>
              </div>
            </Card>
          </div>
          <div className="text-center mt-12" data-id="element-382">
            <Button size="lg" href="/contact" data-id="element-383">
              Get Started
            </Button>
          </div>
        </div>
      </section>
      {/* Key Features Grid */}
      <section className="py-16 bg-light-grey" data-id="element-384">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-385">
          <div className="text-center mb-16" data-id="element-386">
            <h2 className="text-3xl font-bold text-navy font-heading mb-4" data-id="element-387">
              Everything You Need
            </h2>
            <p className="text-lg text-dark max-w-3xl mx-auto" data-id="element-388">
              Artisanfix provides all the tools to help families and carers stay
              organised.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-389">
            {features.map((feature, index) => <Card key={index} className="hover:shadow-lg transition-shadow" data-id="element-390">
                <div className="flex items-start" data-id="element-391">
                  <div className="flex-shrink-0" data-id="element-392">
                    <div className="rounded-full bg-white p-3 w-12 h-12 flex items-center justify-center" data-id="element-393">
                      <feature.icon className="h-6 w-6 text-navy" data-id="element-394" />
                    </div>
                  </div>
                  <div className="ml-4" data-id="element-395">
                    <h3 className="text-lg font-bold text-navy mb-2" data-id="element-396">
                      {feature.title}
                    </h3>
                    <p className="text-dark text-sm" data-id="element-397">{feature.description}</p>
                  </div>
                </div>
              </Card>)}
          </div>
          <div className="text-center mt-12" data-id="element-398">
            <Button href="/features" variant="outline" data-id="element-399">
              View All Features
            </Button>
          </div>
        </div>
      </section>
      {/* Testimonials */}
      <section className="py-16 bg-white" data-id="element-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-401">
          <div className="text-center mb-16" data-id="element-402">
            <h2 className="text-3xl font-bold text-navy font-heading mb-4" data-id="element-403">
              What Families Say
            </h2>
            <p className="text-lg text-dark max-w-3xl mx-auto" data-id="element-404">
              Real experiences from people who use Artisanfix every day.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-405">
            {testimonials.map((testimonial, index) => <Testimonial key={index} quote={testimonial.quote} author={testimonial.author} data-id="element-406" />)}
          </div>
        </div>
      </section>
      {/* Trust Strip */}
      <section className="py-8 bg-light-grey border-t border-b border-light-grey" data-id="element-407">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-408">
          <div className="flex flex-wrap justify-center items-center gap-8" data-id="element-409">
            {trustMarkers.map((marker, index) => <div key={index} className="flex items-center" data-id="element-410">
                <CheckIcon className="h-5 w-5 text-sky-blue mr-2" data-id="element-411" />
                <span className="text-dark font-medium" data-id="element-412">{marker}</span>
              </div>)}
          </div>
        </div>
      </section>
      {/* Final CTA */}
      <section className="bg-navy text-white py-16" data-id="element-413">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-414">
          <h2 className="text-3xl font-bold font-heading mb-4" data-id="element-415">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-white/80 max-w-3xl mx-auto mb-8" data-id="element-416">
            Join thousands of families across the UK who trust Artisanfix for
            their health reminder needs.
          </p>
          <Button variant="secondary" size="lg" href="/contact" data-id="element-417">
            Get Started
          </Button>
        </div>
      </section>
    </div>;
}